import React, { ReactNode, Children } from "react";
import { AnimatePresence, HTMLMotionProps, motion, usePresenceData } from "motion/react";

interface AnimateExitProps {
  children: ReactNode;
  isChildVisible: boolean;
  loading: boolean;
  size?: "md" | "sm";
}

export const AnimateExit: React.FC<AnimateExitProps> & {
  Skeleton: typeof Skeleton;
  Content: typeof Content;
} = ({ children, loading, isChildVisible, size = "md" }) => {
  // Extract AnimateExit.Content and AnimateExit.Skeleton from children
  const content = Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Content,
  );

  let skeleton = Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Skeleton,
  );

  // Use default skeleton if none is provided
  if (!skeleton) {
    skeleton = (
      <Skeleton>
        <div className="px-3 py-2 space-y-2">
          {size === "md" && <div className="skeleton-loader h-4 rounded-md w-2/5"></div>}
          <div className="skeleton-loader h-4 rounded-md w-full"></div>
        </div>
      </Skeleton>
    );
  }

  return (
    <AnimatePresence mode="wait" custom={isChildVisible}>
      {loading
        ? React.cloneElement(skeleton as React.ReactElement)
        : React.cloneElement(content as React.ReactElement, {
          isChildVisible: isChildVisible,
        })}
    </AnimatePresence>
  );
};

// Skeleton Component
const Skeleton = React.forwardRef<
  HTMLDivElement,
  { children: ReactNode; motionProps?: HTMLMotionProps<"div"> }
>(({ children, motionProps = {} }, ref) => {
  const isChildVisible = usePresenceData();
  const exitStyle = isChildVisible ? { opacity: 0, height: "auto" } : { opacity: 0, height: 0 };
  const transition = isChildVisible ? motionProps?.transition ?? { duration: 0 } : { duration: 0.4 };

  return (
    <motion.div
      ref={ref}
      key="loading-skeleton"
      initial={{ opacity: 1, height: "auto" }}
      exit={exitStyle}
      transition={transition}
      {...motionProps}>
      {children}
    </motion.div>
  );
});
Skeleton.displayName = "AnimateExitSkeleton";

// Content Component
const Content: React.FC<{
  children: ReactNode;
  motionProps?: HTMLMotionProps<"div">;
}> = ({ children, motionProps = {} }) => {
  const isChildVisible = usePresenceData();
  return (
    <motion.div
      key="loaded-content"
      initial={{ opacity: 0, height: "auto" }}
      animate={{
        opacity: 1,
        height: "auto",
      }}
      transition={{ duration: 0 }}
      {...motionProps}>
      {isChildVisible ? children : <></>}
    </motion.div>
  );
};
Content.displayName = "AnimateExitContent";

AnimateExit.Skeleton = Skeleton;
AnimateExit.Content = Content;

export default AnimateExit;
